import type { ContainerViewModel, ContainerLayoutViewModel, WidgetTabViewModel, WorkspaceViewModel } from '../types';
import type { SetMaximizedContainerAction } from './container';
import type { Workspace } from '@thinkalpha/ui-types/contracts/workspace';
import type { WorkspaceSettingsUpdate } from 'src/services/WorkspaceService';

export type OnRemoveWorkspace = { type: 'removeWorkspaceEvent' };

export const removeWorkspace = (): OnRemoveWorkspace => ({
    type: 'removeWorkspaceEvent',
});

export type SaveCurrentWorkspaceAction = {
    type: 'saveWorkspaceEvent';
    saveType: 'save' | 'saveAs';
};

export const saveWorkspace = (saveType: SaveCurrentWorkspaceAction['saveType']): SaveCurrentWorkspaceAction => ({
    type: 'saveWorkspaceEvent',
    saveType,
});

export type SaveWorkspaceAsPrimaryAction = {
    type: 'saveWorkspaceAsPrimary';
    id: string;
};

export const makeWorkspacePrimary = (id: SaveWorkspaceAsPrimaryAction['id']): SaveWorkspaceAsPrimaryAction => ({
    type: 'saveWorkspaceAsPrimary',
    id,
});

export type CreateWorkspaceAction = {
    type: 'createBlankWorkspaceEvent';
};

export const createBlankWorkspace = (): CreateWorkspaceAction => ({
    type: 'createBlankWorkspaceEvent',
});

export type SubmitNameForSavingAction = {
    type: 'submitNameForSaving';
    name: string;
    promoteToDefault: boolean;
};

export const submitNameForSaving = (name: string, promoteToDefault: boolean): SubmitNameForSavingAction => {
    return {
        type: 'submitNameForSaving',
        name,
        promoteToDefault,
    };
};

export type SetMosaicTemplateAction = {
    type: 'setMosaicTemplate';
    template: WorkspaceViewModel['mosaicTemplate'];
};

export const setMosaicTemplate = (template: WorkspaceViewModel['mosaicTemplate']): SetMosaicTemplateAction => ({
    type: 'setMosaicTemplate',
    template,
});

export type MarkMosaicTemplateSlotOccupiedAction = {
    type: 'markMosaicTemplateSlotOccupied';
    slotId: string;
    containerId: string;
};

export const markMosaicTemplateSlotOccupied = (
    slotId: string,
    containerId: string,
): MarkMosaicTemplateSlotOccupiedAction => ({
    type: 'markMosaicTemplateSlotOccupied',
    slotId,
    containerId,
});

export type UnmarkMosaicTemplateSlotOccupiedAction = {
    type: 'unmarkMosaicTemplateSlotOccupied';
    slotId: string;
};

export const unmarkMosaicTemplateSlotOccupied = (slotId: string): UnmarkMosaicTemplateSlotOccupiedAction => ({
    type: 'unmarkMosaicTemplateSlotOccupied',
    slotId,
});

export type WorkspaceEvent =
    | CreateWorkspaceAction
    | OnRemoveWorkspace
    | SaveCurrentWorkspaceAction
    | SubmitNameForSavingAction;

export type InitializeWorkspaceData = {
    type: 'initializeWorkspace';
    workspace: WorkspaceViewModel;
    containers: ContainerViewModel[];
    layouts: ContainerLayoutViewModel[];
    tabs: WidgetTabViewModel[];
};

export const initializeNormalizedData = (
    workspace: WorkspaceViewModel,
    containers: ContainerViewModel[],
    layouts: ContainerLayoutViewModel[],
    tabs: WidgetTabViewModel[],
): InitializeWorkspaceData => ({
    type: 'initializeWorkspace',
    workspace,
    containers,
    layouts,
    tabs,
});

export type SetSavingTrueAction = { type: 'setSavingTrueAction' };

export const setSavingTrue = (): SetSavingTrueAction => ({
    type: 'setSavingTrueAction',
});

/** Save acknowledgment is separate from loading: widget reducers retain state unless they opt in. */
export interface SavedWorkspaceLoadedAction {
    type: 'savedWorkspaceLoaded';
    submitted: Workspace;
    saved: Workspace;
    isDefaultWorkspace: boolean;
    hasUnsavedChanges: boolean;
}

export const savedWorkspaceLoaded = (data: Omit<SavedWorkspaceLoadedAction, 'type'>): SavedWorkspaceLoadedAction => ({
    type: 'savedWorkspaceLoaded',
    ...data,
});

export type WorkspaceSavingFailedAction = { type: 'workspaceSavingFailed' };

export const workspaceSavingFailed = (): WorkspaceSavingFailedAction => ({
    type: 'workspaceSavingFailed',
});

export type MarkWorkspaceDirtyAction = { type: 'markWorkspaceDirty' };

export const markWorkspaceDirty = (): MarkWorkspaceDirtyAction => ({
    type: 'markWorkspaceDirty',
});

export type SavingCancelledAction = { type: 'savingCancelledAction' };

export const savingCancelled = (): SavingCancelledAction => ({
    type: 'savingCancelledAction',
});

export type SetResearchingWorkspaceAction = {
    type: 'setResearchingWorkspace';
};

export const setResearchingWorkspace = (): SetResearchingWorkspaceAction => ({
    type: 'setResearchingWorkspace',
});

export type CompleteWorkspaceResearchAction = {
    type: 'completeWorkspaceResearch';
};

export const completeWorkspaceResearch = (): CompleteWorkspaceResearchAction => ({
    type: 'completeWorkspaceResearch',
});

/**
 * Compatibility mirror for Redux state. Workspace settings orchestration lives in
 * WorkspaceService.updateSettings so the reducer no longer drives saving.
 */
export type UpdateWorkspaceSettings = {
    type: 'updateWorkspaceSettings';
    settings: WorkspaceSettingsUpdate;
};

export const updateWorkspaceSettings = (settings: UpdateWorkspaceSettings['settings']): UpdateWorkspaceSettings => ({
    type: 'updateWorkspaceSettings',
    settings,
});

export type ClearWorkspaceStateAction = { type: 'clearWorkspaceState' };

export const clearWorkspaceState = (): ClearWorkspaceStateAction => ({
    type: 'clearWorkspaceState',
});

export type InitializeUnsavedWorkspaceAction = {
    type: 'initializeUnsavedWorkspace';
    enableMosaicLayout: boolean;
    mosaicTemplate?: WorkspaceViewModel['mosaicTemplate'];
    name?: string;
    gridSnappingEnabled?: boolean;
    mosaicGutterPx?: number;
    newWorkspaceRequestId?: string;
};

export const initializeUnsavedWorkspace = (
    enableMosaicLayout: boolean,
    mosaicTemplate?: WorkspaceViewModel['mosaicTemplate'],
    overrides?: {
        name?: string;
        gridSnappingEnabled?: boolean;
        mosaicGutterPx?: number;
        newWorkspaceRequestId?: string;
    },
): InitializeUnsavedWorkspaceAction => ({
    type: 'initializeUnsavedWorkspace',
    enableMosaicLayout,
    mosaicTemplate,
    ...overrides,
});

export type ToggleLayoutModeAction = { type: 'toggleLayoutMode' };

export const toggleLayoutMode = (): ToggleLayoutModeAction => ({
    type: 'toggleLayoutMode',
});

export type SetLayoutModeLocalAction = { type: 'setLayoutModeLocal'; enableMosaicLayout: boolean };

export const setLayoutModeLocal = (enableMosaicLayout: boolean): SetLayoutModeLocalAction => ({
    type: 'setLayoutModeLocal',
    enableMosaicLayout,
});

export type SetWorkspaceErrorAction = {
    type: 'setWorkspaceError';
    errorType: 'permission' | 'generic';
};

export const setWorkspaceError = (errorType: SetWorkspaceErrorAction['errorType']): SetWorkspaceErrorAction => ({
    type: 'setWorkspaceError',
    errorType,
});

export type WorkspaceAction =
    | UpdateWorkspaceSettings
    | CompleteWorkspaceResearchAction
    | SavedWorkspaceLoadedAction
    | InitializeWorkspaceData
    | SetResearchingWorkspaceAction
    | SetSavingTrueAction
    | WorkspaceEvent
    | WorkspaceSavingFailedAction
    | MarkWorkspaceDirtyAction
    | SavingCancelledAction
    | SetMaximizedContainerAction
    | ClearWorkspaceStateAction
    | InitializeUnsavedWorkspaceAction
    | SetWorkspaceErrorAction
    | SetMosaicTemplateAction
    | MarkMosaicTemplateSlotOccupiedAction
    | UnmarkMosaicTemplateSlotOccupiedAction
    | ToggleLayoutModeAction
    | SetLayoutModeLocalAction;
